import _ from 'lodash';
import { confirmLexicalStateInTextComponent, findTextRelationByDocumentId } from 'modules/draftjs2lexical';
import * as lexical from 'modules/Lexical/helpers';
import guid from 'uuid';
import { Layer } from 'const';
import * as Models from 'models';
import { isImage, isReferenceCitation, isTextComponent } from 'utils/entityType';
import { findImageDuplicateId, findReferenceCitationDuplicateId } from 'utils/findDocumentDuplicateId';
import { toImmutable } from 'utils/immutable';
import { isRegularRelation } from 'utils/relations/isRegularRelation';

type Args = {
  activeLayer: Layer;
  layoutDocument: Models.ReusableLayout;
  projectBrandDefinition: Models.OnlineBrandDefinition;
  projectDocuments: Models.CombinedDocuments;
  documentsToSet: Models.CombinedDocuments;
};

export function checkForDuplicates(arg: Args): Args {
  const {
    projectDocuments,
    layoutDocument,
  } = arg;
  const {
    documents,
    entities,
    entities: {
      brandDefinition: extendedBrandDefinition,
      layoutSourceBrandDefinition,
      relations,
      styles,
    },
  } = layoutDocument;

  const safeLayoutSourceBrandDefinition = layoutSourceBrandDefinition || extendedBrandDefinition;
  const layoutFonts = toImmutable(safeLayoutSourceBrandDefinition.fonts);

  const projectImages = _(projectDocuments)
    .pickBy<Models.Image>(isImage)
    .value();
  const projectReferenceCitations = _(projectDocuments)
    .pickBy<Models.ReferenceCitation>(isReferenceCitation)
    .value();

  const replacedTextComponentIds: string[] = [];
  const newDocIdByOldId: Record<string, string> = {};
  const getNewDocId = (id: string): string => newDocIdByOldId[id] || id;

  // skip document if duplicated one was found
  const documentsToSet = _(documents)
    .pickBy((document, id) => {
      // we specially check images and references before texts
      if (!isReferenceCitation(document) && !isImage(document)) {
        return true;
      }

      const duplicateId = isImage(document)
        ? findImageDuplicateId(document, projectImages)
        : findReferenceCitationDuplicateId(document, projectReferenceCitations);

      newDocIdByOldId[id] = duplicateId;

      return !duplicateId;
    })
    .pickBy((document, id) => {
      if (!document || !isTextComponent(document)) {
        return true;
      }

      // replace all reference citation ids by duplicated ones
      const documnetWithLexicalState = confirmLexicalStateInTextComponent(
        document,
        findTextRelationByDocumentId(relations, id)?.styles ?? null,
        layoutFonts,
      );
      const { lexicalState, referenceCitations } = lexical.replaceReferenceCitationsOnComponent(
        documnetWithLexicalState,
        newDocIdByOldId,
      );
      document.referenceCitations = referenceCitations;
      document.lexicalState = lexicalState;

      return true;
    })
    .toPairs()
    .map<[string, Models.CombinedDocument]>(([id, document]) => {
    // potentially there are RLs on Production env which have text components and relations with the same identifiers (within each RL)
    // to be sure they won't override each other, we have to change the identifiers of those entities if such RL has been already added to a project
    if (!isTextComponent(document) || !projectDocuments[id]) {
      return [id, document];
    }

    const newId = guid();
    newDocIdByOldId[id] = newId;
    replacedTextComponentIds.push(id);

    document.id = newId;

    return [newId, document];
  })
    .fromPairs()
    .value();

  const newRelIdByOldId: Record<string, string> = {};
  const getNewRelId = (id: string): string => newRelIdByOldId[id] || id;

  const relationsToSet = _(relations)
    .toPairs()
    .map<[string, Models.Relation]>(([id, relation]) => {
    if (!isRegularRelation(relation) || !replacedTextComponentIds.includes(relation.documentId)) {
      return [id, relation];
    }

    const newId = guid();
    newRelIdByOldId[id] = newId;
    relation.id = newId;

    return [newId, relation];
  })
    .fromPairs()
    .mapValues((relation) => {
      if (!isRegularRelation(relation)) {
        relation.relationIds = relation.relationIds.map(getNewRelId);

        return relation;
      }

      const { documentId } = relation;
      const relationStyles = relation.styles as Models.CommonStyles;
      relation.documentId = getNewDocId(documentId);

      // replace background image ids
      if (relationStyles?.backgroundImage?.id) {
        relationStyles.backgroundImage.id = getNewDocId(relationStyles.backgroundImage.id);
      }

      return relation;
    })
    .value();

  // replace background image id in layout styles
  if (styles?.backgroundImage?.id) {
    styles.backgroundImage.id = getNewDocId(styles.backgroundImage.id);
  }
  entities.relationId = getNewRelId(entities.relationId);

  // replace only text documents
  layoutDocument.documents = {
    ..._.pickBy(documentsToSet, isTextComponent),
    ..._.omitBy(layoutDocument.documents, isTextComponent),
  };
  arg.documentsToSet = documentsToSet;
  layoutDocument.entities.relations = relationsToSet;

  return arg;
}
